/**
 * Public Patient View for Exercise Routines
 * No auth — accessed via /exercises/:token
 * Patient can mark exercises as done; admin sees count via completions array.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dumbbell, Check, Loader2, Sparkles, Clock, Target, Calendar,
  Repeat, Layers, CheckCircle2, Heart,
} from "lucide-react";

const FREQ_LABELS = {
  daily:        "Quotidiana",
  every_2_days: "A giorni alterni",
  weekly:       "Settimanale",
  custom:       "Personalizzata",
};

const PublicExerciseRoutine = () => {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState({});

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await axios.get(`${API}/wellness/exercises/routines/public/${token}`);
      setData(r.data);
    } catch (err) {
      setData({ error: err.response?.status === 404 ? "Link non valido o scaduto" : "Errore" });
    } finally {
      setLoad(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (code) => {
    setBusy(b => ({ ...b, [code]: true }));
    try {
      await axios.post(`${API}/wellness/exercises/routines/public/${token}/complete`, { exercise_code: code });
      toast.success("Ottimo lavoro!");
      load();
    } catch {
      toast.error("Riprova tra un momento");
    } finally {
      setBusy(b => ({ ...b, [code]: false }));
    }
  };

  const doneTodayCodes = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return new Set((data?.routine?.completions || [])
      .filter(c => (c.date || "").slice(0, 10) === today)
      .map(c => c.exercise_code));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-emerald-50">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-rose-100 flex items-center justify-center mb-4">
            <Sparkles className="w-7 h-7 text-rose-500" />
          </div>
          <h1 className="text-2xl font-black text-slate-900">Ops…</h1>
          <p className="text-sm text-slate-500 mt-2">{data.error}</p>
        </div>
      </div>
    );
  }

  const r = data.routine;
  const progress = r.progress_today_pct;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50" data-testid="public-routine-page">
      {/* HERO */}
      <header className="max-w-2xl mx-auto px-4 pt-10 pb-6">
        <div className="rounded-3xl bg-white shadow-xl border border-slate-200/60 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <Dumbbell className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-600">
                {data.tenant_name}
              </p>
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight" data-testid="public-routine-name">
                {r.name}
              </h1>
              {data.client_name && (
                <p className="text-sm text-slate-500 mt-0.5">Per <span className="font-bold">{data.client_name}</span></p>
              )}
            </div>
          </div>

          {r.description && (
            <p className="text-sm text-slate-600 mb-4 italic">"{r.description}"</p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Stat icon={Repeat}  label="Frequenza" value={FREQ_LABELS[r.frequency] || r.frequency} />
            <Stat icon={Layers}  label="Esercizi"  value={r.total_exercises} />
            <Stat icon={Calendar} label="Durata"    value={`${r.duration_days}g`} />
          </div>

          {/* Progress */}
          <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 p-3">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Progresso di oggi</p>
              <p className="text-lg font-black text-slate-900 font-variant-numeric tabular-nums">
                {r.completed_today}/{r.total_exercises}
              </p>
            </div>
            <div className="h-2 w-full rounded-full bg-white overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            {progress === 100 && (
              <p className="text-xs text-emerald-700 font-bold mt-2 flex items-center gap-1">
                <Heart className="w-3.5 h-3.5" /> Hai completato tutto! Ottimo lavoro 💪
              </p>
            )}
          </div>
        </div>
      </header>

      {/* EXERCISES */}
      <main className="max-w-2xl mx-auto px-4 pb-12 space-y-2.5" data-testid="public-routine-list">
        {r.exercises.map((ex, i) => {
          const done = doneTodayCodes.has(ex.code);
          const isBusy = busy[ex.code];
          return (
            <div
              key={ex.code}
              className={`rounded-2xl bg-white shadow-sm border-2 transition-all p-4 ${
                done ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200/60"
              }`}
              data-testid={`public-exercise-${ex.code}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-amber-100 to-emerald-100 flex items-center justify-center text-amber-700 font-black">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900">{ex.name}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-0.5">
                      <Target className="w-3 h-3" /> {ex.sets} serie × {ex.reps} ripetizioni
                    </span>
                    {ex.hold_sec > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Clock className="w-3 h-3" /> {ex.hold_sec}s
                      </span>
                    )}
                  </div>
                  {ex.description && (
                    <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{ex.description}</p>
                  )}
                  {ex.note && (
                    <p className="text-xs italic text-amber-700 mt-1.5 bg-amber-50 rounded-lg px-2 py-1 border border-amber-200">
                      Nota: {ex.note}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {done ? (
                  <div className="inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Fatto oggi
                  </div>
                ) : (
                  <Button
                    onClick={() => markDone(ex.code)}
                    disabled={isBusy}
                    className="w-full sm:w-auto rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 hover:opacity-90 text-white"
                    data-testid={`public-exercise-done-${ex.code}`}
                  >
                    {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Segna come fatto
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </main>

      <footer className="text-center pb-6">
        <p className="text-[10px] text-slate-400">Powered by <span className="font-bold">SmartBooker · Smart Wellness</span></p>
      </footer>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200/60 p-2 text-center">
    <Icon className="w-3.5 h-3.5 mx-auto text-slate-400 mb-0.5" />
    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-xs font-black text-slate-900 mt-0.5">{value}</p>
  </div>
);

export default PublicExerciseRoutine;
